import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getCentralToday } from "@/utils/dateHelpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription,
} from "@/components/ui/dialog";
import {
  DoorOpen, Plus, Pencil, Trash2, Calendar, CheckCircle, XCircle, RefreshCw,
  Lock, Unlock, MapPin, Clock, ArrowUpDown, ArrowUp, ArrowDown, Search, Copy, Mail, Users,
  AlertCircle, Eye, Ban, UserX, Info, Droplets, Sparkles, ChevronDown, ChevronUp,
  Hotel, BedDouble, BedSingle, Home, Edit, Bed, Bell, Check, LogIn, LogOut,
  MessageCircle, Phone, TrendingUp, X, ArrowLeftRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon, label, value, testId }) {
  return (
    <div className="stat-card rounded-lg" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-vault-gold/80 mb-2">{label}</p>
          <p className="font-outfit text-4xl font-bold text-vault-text">{value}</p>
        </div>
        <div className="text-vault-gold/50">{icon}</div>
      </div>
    </div>
  );
}

export function RoomsTab({
  rooms, fetchRooms,
  showRoomDialog, setShowRoomDialog, editingRoom, setEditingRoom, roomForm, setRoomForm,
  roomSortOrder, setRoomSortOrder, deleteRoomConfirm, setDeleteRoomConfirm,
  blockedRooms, blockRoomForm, setBlockRoomForm, showBlockRoomDialog, setShowBlockRoomDialog, blockedRoomStats,
  reservations, bitsyBookings, showBitsyBookings, setShowBitsyBookings,
  editingBitsyBooking, setEditingBitsyBooking, bitsyEditForm, setBitsyEditForm,
  nonRailroadHistory, showNonRailroadHistory, setShowNonRailroadHistory,
  checkingInBitsyBooking, setCheckingInBitsyBooking, bitsyCheckInRoom, setBitsyCheckInRoom,
  showMultiRoomBooking, setShowMultiRoomBooking, multiRoomForm, setMultiRoomForm,
  selectedRooms, setSelectedRooms, multiRoomLoading,
  lastConfirmation, setLastConfirmation,
  blockedGuests, showBlockedGuests, setShowBlockedGuests,
  blockGuestDialog, setBlockGuestDialog, blockGuestForm, setBlockGuestForm,
  showEditGuestDialog, editGuestForm, setEditGuestForm,
  checkinDialog, setCheckinDialog,
  handleCreateRoom, handleEditRoom, handleUpdateRoom, handleDeleteRoom,
  handleBlockRoom, handleUnblockRoom, handleMarkRoomDirty, handleMarkRoomClean,
  fetchBlockedRooms, fetchBlockedRoomStats, fetchReservations, fetchBitsyBookings,
  handleEditBitsyBooking, handleSaveBitsyBooking, handleCancelBitsyBooking, handleCheckInBitsyBooking,
  fetchNonRailroadHistory, handleEditGuest, handleSaveGuestEdit,
  handleBlockGuest, handleUnblockGuest, handleQuickBlockGuest,
  addRoomToBooking, removeRoomFromBooking, updateRoomInBooking, handleMultiRoomBooking,
  copyConfirmationToClipboard, openCheckinDialog, handleCheckinReservation,
  handleCancelReservation, handleConfirmReservation, handleSendConfirmationEmail, fetchBlockedGuests,
  fetchStats
}) {
  // Expected arrivals/checkouts state (managed locally)
  const [expectedArrivals, setExpectedArrivals] = useState([]);
  const [expectedCheckouts, setExpectedCheckouts] = useState([]);
  const [checkingCpkcEmails, setCheckingCpkcEmails] = useState(false);
  // Per-card date filter for Non-Railroad Guest History — independent of the
  // admin-wide date pickers used by the Billing/Records tabs.
  const [nonRailStartDate, setNonRailStartDate] = useState("");
  const [nonRailEndDate, setNonRailEndDate] = useState("");

  useEffect(() => {
    fetchExpectedArrivals();
    fetchExpectedCheckouts();
  }, []);

  const fetchExpectedArrivals = async () => {
    try {
      const response = await axios.get(`${API}/admin/expected-arrivals`);
      setExpectedArrivals(response.data);
    } catch (error) { console.error("Failed to fetch expected arrivals"); }
  };

  const fetchExpectedCheckouts = async () => {
    try {
      const response = await axios.get(`${API}/admin/expected-checkouts`);
      setExpectedCheckouts(response.data);
    } catch (error) { console.error("Failed to fetch expected checkouts"); }
  };

  const checkCpkcEmails = async () => {
    setCheckingCpkcEmails(true);
    try {
      await axios.post(`${API}/admin/check-cpkc-emails`);
      toast.success("Email check completed!");
      fetchExpectedArrivals();
      fetchExpectedCheckouts();
    } catch (error) { toast.error("Failed to check emails"); }
    setCheckingCpkcEmails(false);
  };

  const deleteExpectedArrival = async (arrivalId) => {
    try {
      await axios.delete(`${API}/admin/expected-arrivals/${arrivalId}`);
      toast.success("Arrival removed");
      fetchExpectedArrivals();
    } catch (error) { toast.error("Failed to remove arrival"); }
  };

  const deleteExpectedCheckout = async (checkoutId) => {
    try {
      await axios.delete(`${API}/admin/expected-checkouts/${checkoutId}`);
      toast.success("Check-out removed");
      fetchExpectedCheckouts();
    } catch (error) { toast.error("Failed to remove check-out"); }
  };

  const markCheckoutCompleted = async (checkoutId) => {
    try {
      await axios.post(`${API}/admin/expected-checkouts/${checkoutId}/completed`);
      toast.success("Check-out marked as completed");
      fetchExpectedCheckouts();
    } catch (error) { toast.error("Failed to mark check-out as completed"); }
  };

  return (
    <>
              {/* Header with Add Room Button */}
              <div className="mb-6">
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Room Management</h1>
                    <p className="text-vault-text-secondary font-manrope mt-1">Add, edit, and manage hotel rooms</p>
                  </div>
                </div>
                {/* Add Room Button - Separate row for visibility */}
                <div className="mt-4 flex gap-2">
                  <Button 
                    onClick={() => {
                      setEditingRoom(null);
                      setRoomForm({ room_number: "", room_type: "Standard", floor: "1", notes: "" });
                      setShowRoomDialog(true);
                    }}
                    className="vault-btn-primary flex items-center gap-2"
                    data-testid="add-room-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Room
                  </Button>
                  <Button 
                    onClick={() => {
                      // Reset form when opening dialog
                      setBlockRoomForm({ 
                        room_number: "", 
                        room_type: "",
                        guest_name: "", 
                        email: "",
                        phone: "",
                        address: "",
                        check_in_date: getCentralToday(),
                        check_out_date: "",
                        room_rate: "",
                        notes: "",
                        is_reservation: false
                      });
                      setShowBlockRoomDialog(true);
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2"
                    data-testid="block-room-btn"
                  >
                    <Users className="w-4 h-4" />
                    Non-Railroad Guest
                  </Button>
                  <Button 
                    onClick={() => {
                      setMultiRoomForm({
                        guest_name: '',
                        email: '',
                        phone: '',
                        address: '',
                        check_in_date: '',
                        check_out_date: '',
                        notes: '',
                        send_confirmation_email: true
                      });
                      setSelectedRooms([]);
                      setLastConfirmation(null);
                      setShowMultiRoomBooking(true);
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
                    data-testid="multi-room-btn"
                  >
                    <Home className="w-4 h-4" />
                    Multi-Room Booking
                  </Button>
                  <Button 
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/rooms/send-dirty-list`);
                        toast.success("Dirty rooms list sent to Telegram");
                      } catch (error) {
                        toast.error("Failed to send dirty list");
                      }
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
                    data-testid="send-dirty-list-btn"
                  >
                    <Bell className="w-4 h-4" />
                    Send Dirty List to Telegram
                  </Button>
                  <Button 
                    onClick={() => {
                      fetchBitsyBookings();
                      setShowBitsyBookings(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                    data-testid="bitsy-bookings-btn"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Bitsy Bookings ({bitsyBookings.length})
                  </Button>
                </div>
              </div>

              {/* Room Stats — use unified backend-computed values from blocked-stats
                  so the top row and the Total Occupancy Overview below never drift
                  apart (historical bug: rooms.status got out of sync with bookings). */}
              <div className="grid grid-cols-2 md:grid-cols-7 gap-4 mb-6">
                <StatCard 
                  icon={<Bed className="w-6 h-6" />}
                  label="Total Rooms"
                  value={blockedRoomStats?.total_rooms ?? rooms.length}
                  testId="stat-total-rooms"
                />
                <StatCard 
                  icon={<CheckCircle className="w-6 h-6" />}
                  label="Available"
                  value={blockedRoomStats?.vacant_rooms ?? '—'}
                  testId="stat-available-rooms"
                />
                <StatCard 
                  icon={<DoorOpen className="w-6 h-6" />}
                  label="Railroad Guests"
                  value={blockedRoomStats?.railroad_occupied ?? '—'}
                  testId="stat-occupied-rooms"
                />
                <StatCard 
                  icon={<Users className="w-6 h-6" />}
                  label="Other Guests"
                  value={blockedRoomStats?.active_blocked_rooms ?? '—'}
                  testId="stat-blocked-rooms"
                />
                <StatCard 
                  icon={<Calendar className="w-6 h-6" />}
                  label="Reserved"
                  value={blockedRoomStats?.reserved_rooms ?? 0}
                  testId="stat-reserved-rooms"
                />
                <StatCard 
                  icon={<AlertCircle className="w-6 h-6" />}
                  label="Dirty"
                  value={blockedRoomStats?.dirty_rooms ?? 0}
                  testId="stat-dirty-rooms"
                />
                <StatCard 
                  icon={<XCircle className="w-6 h-6" />}
                  label="Maintenance"
                  value={blockedRoomStats?.maintenance_rooms ?? 0}
                  testId="stat-maintenance-rooms"
                />
              </div>

              {/* Total Occupancy Overview */}
              {blockedRoomStats && (
                <Card className="bg-vault-surface border-vault-border mb-6">
                  <CardHeader className="border-b border-vault-border pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-outfit text-lg text-vault-gold flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Total Occupancy Overview
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/admin/occupancy/record`);
                              toast.success("Today's occupancy recorded!");
                            } catch (error) {
                              toast.error("Failed to record occupancy");
                            }
                          }}
                          size="sm"
                          className="bg-green-600 hover:bg-green-500 text-white"
                          data-testid="record-occupancy-btn"
                        >
                          Record Today
                        </Button>
                        <Button
                          onClick={() => window.open(withAdminAuthToken(`${API}/admin/occupancy/export-pdf?days=30`), '_blank')}
                          size="sm"
                          variant="outline"
                          className="border-vault-gold text-vault-gold hover:bg-vault-gold/10"
                          data-testid="export-occupancy-btn"
                        >
                          Export Report
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                      <div className="bg-gradient-to-br from-vault-gold/20 to-amber-900/20 border border-vault-gold/30 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-vault-gold">{blockedRoomStats.occupancy_percent}%</p>
                        <p className="text-vault-text-secondary text-sm font-medium">Total Occupancy</p>
                        <p className="text-vault-text-secondary text-xs mt-1">
                          {blockedRoomStats.total_occupied}/{blockedRoomStats.total_rooms} rooms
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-500/20 to-blue-900/20 border border-blue-500/30 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-blue-400">{blockedRoomStats.railroad_occupied}</p>
                        <p className="text-vault-text-secondary text-sm font-medium">Railroad Guests</p>
                        <p className="text-blue-400/70 text-xs mt-1">
                          {blockedRoomStats.total_rooms > 0 ? Math.round(blockedRoomStats.railroad_occupied / blockedRoomStats.total_rooms * 100) : 0}% of rooms
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-amber-500/20 to-amber-900/20 border border-amber-500/30 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-amber-400">{blockedRoomStats.active_blocked_rooms}</p>
                        <p className="text-vault-text-secondary text-sm font-medium">Non-Railroad</p>
                        <p className="text-amber-400/70 text-xs mt-1">
                          {blockedRoomStats.blocked_occupancy_percent}% of rooms
                        </p>
                      </div>
                      <div
                        className="bg-gradient-to-br from-yellow-500/20 to-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 text-center"
                        title={
                          (blockedRoomStats.reserved_room_numbers || []).length > 0
                            ? `Rooms: ${(blockedRoomStats.reserved_room_numbers || []).join(', ')}`
                            : 'No rooms reserved for arrival today'
                        }
                        data-testid="overview-reserved-card"
                      >
                        <p className="text-3xl font-bold text-yellow-400">{blockedRoomStats.reserved_rooms ?? 0}</p>
                        <p className="text-vault-text-secondary text-sm font-medium">Reserved (arriving)</p>
                        <p className="text-yellow-400/70 text-xs mt-1 truncate">
                          {(blockedRoomStats.reserved_room_numbers || []).length > 0
                            ? `Rooms: ${(blockedRoomStats.reserved_room_numbers || []).join(', ')}`
                            : 'None'}
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-green-500/20 to-green-900/20 border border-green-500/30 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-green-400">{blockedRoomStats.vacant_rooms ?? (blockedRoomStats.total_rooms - blockedRoomStats.total_occupied)}</p>
                        <p className="text-vault-text-secondary text-sm font-medium">Vacant Rooms</p>
                        <p className="text-green-400/70 text-xs mt-1">
                          {blockedRoomStats.total_rooms > 0 ? Math.round(((blockedRoomStats.vacant_rooms ?? 0)) / blockedRoomStats.total_rooms * 100) : 0}% available
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-900/20 border border-emerald-500/30 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-emerald-400">${blockedRoomStats.total_revenue_all_time.toLocaleString()}</p>
                        <p className="text-vault-text-secondary text-sm font-medium">Non-Rail Revenue</p>
                        <p className="text-emerald-400/70 text-xs mt-1">All time</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Expected CPKC Arrivals Section */}
              <Card className="bg-blue-900/20 border-blue-500/50 mb-6">
                <CardHeader className="border-b border-blue-500/30 pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-outfit text-lg text-blue-400 flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Expected CPKC Arrivals
                      {expectedArrivals.length > 0 && (
                        <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full ml-2">
                          {expectedArrivals.length}
                        </span>
                      )}
                    </CardTitle>
                    <Button
                      onClick={checkCpkcEmails}
                      disabled={checkingCpkcEmails}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-500 text-white"
                      data-testid="check-cpkc-emails-btn"
                    >
                      {checkingCpkcEmails ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Check Emails
                    </Button>
                  </div>
                  <p className="text-blue-300/70 text-xs mt-1">
                    Auto-imported from CPKC email (aces_support@apiglobalsolutions.com) - Checked every 5 minutes
                  </p>
                </CardHeader>
                <CardContent className="p-4">
                  {expectedArrivals.length === 0 ? (
                    <p className="text-vault-text-secondary text-center py-4">No expected arrivals</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-blue-500/30">
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Employee</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Name</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Check-In</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Check-Out</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Status</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expectedArrivals.map((arrival) => (
                            <tr key={arrival.id} className="border-b border-blue-500/20 hover:bg-blue-900/20">
                              <td className="py-2 px-3 text-vault-text font-mono text-sm">{arrival.employee_id || '-'}</td>
                              <td className="py-2 px-3 text-vault-text">{arrival.first_name} {arrival.last_name}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">
                                {arrival.check_in_date} {arrival.check_in_time || ''}
                              </td>
                              <td className="py-2 px-3 text-vault-text-secondary">{arrival.check_out_date || '-'}</td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  arrival.status === 'checked_in' 
                                    ? 'bg-emerald-500/20 text-emerald-400' 
                                    : 'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {arrival.status === 'checked_in' ? 'Checked In' : 'Expected'}
                                </span>
                              </td>
                              <td className="py-2 px-3">
                                {arrival.status === 'expected' && (
                                  <Button
                                    onClick={() => deleteExpectedArrival(arrival.id)}
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                                    title="Remove (cancelled)"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-blue-300/50 text-xs mt-3">
                    Chatbot will not sell rooms when CPKC arrivals are expected. Guests check in at kiosk.
                  </p>
                </CardContent>
              </Card>

              {/* Expected CPKC Check-Outs Section */}
              <Card className="bg-orange-900/20 border-orange-500/50 mb-6">
                <CardHeader className="border-b border-orange-500/30 pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-outfit text-lg text-orange-400 flex items-center gap-2">
                      <LogOut className="w-5 h-5" />
                      Expected CPKC Check-Outs
                      {expectedCheckouts.length > 0 && (
                        <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full ml-2">
                          {expectedCheckouts.length}
                        </span>
                      )}
                    </CardTitle>
                  </div>
                  <p className="text-orange-300/70 text-xs mt-1">
                    Employees expected to check out - rooms will need cleaning
                  </p>
                </CardHeader>
                <CardContent className="p-4">
                  {expectedCheckouts.length === 0 ? (
                    <p className="text-vault-text-secondary text-center py-4">No expected check-outs</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-orange-500/30">
                            <th className="text-left py-2 px-3 text-orange-400 text-sm">Employee</th>
                            <th className="text-left py-2 px-3 text-orange-400 text-sm">Name</th>
                            <th className="text-left py-2 px-3 text-orange-400 text-sm">Check-Out Date</th>
                            <th className="text-left py-2 px-3 text-orange-400 text-sm">Check-Out Time</th>
                            <th className="text-left py-2 px-3 text-orange-400 text-sm">Room</th>
                            <th className="text-left py-2 px-3 text-orange-400 text-sm">Status</th>
                            <th className="text-left py-2 px-3 text-orange-400 text-sm">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expectedCheckouts.map((checkout) => (
                            <tr key={checkout.id} className="border-b border-orange-500/20 hover:bg-orange-900/20">
                              <td className="py-2 px-3 text-vault-text font-mono text-sm">{checkout.employee_id || '-'}</td>
                              <td className="py-2 px-3 text-vault-text">{checkout.first_name} {checkout.last_name}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">{checkout.check_out_date || '-'}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">{checkout.check_out_time || '-'}</td>
                              <td className="py-2 px-3 text-vault-text">{checkout.room_number || '-'}</td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  checkout.status === 'completed' 
                                    ? 'bg-emerald-500/20 text-emerald-400' 
                                    : 'bg-orange-500/20 text-orange-400'
                                }`}>
                                  {checkout.status === 'completed' ? 'Completed' : 'Expected'}
                                </span>
                              </td>
                              <td className="py-2 px-3 flex gap-1">
                                {checkout.status === 'expected' && (
                                  <>
                                    <Button
                                      onClick={() => markCheckoutCompleted(checkout.id)}
                                      size="sm"
                                      variant="ghost"
                                      className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 h-7 px-2"
                                      title="Mark as completed"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      onClick={() => deleteExpectedCheckout(checkout.id)}
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                                      title="Remove"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-orange-300/50 text-xs mt-3">
                    Check-outs are auto-removed after the check-out date passes.
                  </p>
                </CardContent>
              </Card>

              {/* Blocked Rooms (Other Guests) Section */}
              {blockedRooms.length > 0 && (
                <Card className="bg-amber-900/20 border-amber-600/50 mb-6">
                  <CardHeader className="border-b border-amber-600/30 pb-3">
                    <CardTitle className="font-outfit text-lg text-amber-400 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Other Guests - Currently Checked In
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-amber-600/30">
                            <th className="text-left py-2 px-3 text-amber-400 text-sm">Room</th>
                            <th className="text-left py-2 px-3 text-amber-400 text-sm">Guest Name</th>
                            <th className="text-left py-2 px-3 text-amber-400 text-sm">Phone</th>
                            <th className="text-left py-2 px-3 text-amber-400 text-sm">Check-In</th>
                            <th className="text-left py-2 px-3 text-amber-400 text-sm">Check-Out</th>
                            <th className="text-left py-2 px-3 text-amber-400 text-sm">Rate/Night</th>
                            <th className="text-left py-2 px-3 text-amber-400 text-sm">Total</th>
                            <th className="text-left py-2 px-3 text-amber-400 text-sm">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {blockedRooms.map((block) => (
                            <tr key={block.id} className="border-b border-amber-600/20 hover:bg-amber-900/20">
                              <td className="py-2 px-3 text-vault-text font-bold">{block.room_number}</td>
                              <td className="py-2 px-3 text-vault-text">{block.guest_name}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">{block.phone || '-'}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">{block.check_in_date || '-'}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">{block.check_out_date || '-'}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">${block.room_rate || 0}</td>
                              <td className="py-2 px-3 text-green-400 font-medium">${block.total_revenue || 0}</td>
                              <td className="py-2 px-3 flex gap-1">
                                {block.email && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                                    onClick={() => handleSendConfirmationEmail(block.id, block.email)}
                                    title="Send confirmation email"
                                  >
                                    <Mail className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                                  onClick={() => handleEditGuest(block)}
                                  title="Edit guest info"
                                  data-testid={`edit-guest-${block.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                  onClick={() => handleUnblockRoom(block.room_number)}
                                  title="Check out guest"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Check Out
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Future Reservations Section */}
              {reservations.length > 0 && (
                <Card className="bg-blue-900/20 border-blue-600/50 mb-6">
                  <CardHeader className="border-b border-blue-600/30 pb-3">
                    <CardTitle className="font-outfit text-lg text-blue-400 flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Upcoming Reservations ({reservations.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-blue-600/30">
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Room</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Guest Name</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Email</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Phone</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Check-In</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Check-Out</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Total</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Status</th>
                            <th className="text-left py-2 px-3 text-blue-400 text-sm">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reservations.map((res) => (
                            <tr key={res.id} className={`border-b border-blue-600/20 hover:bg-blue-900/20 ${res.confirmation_status === 'confirmed' ? 'bg-green-900/10' : ''}`}>
                              <td className="py-2 px-3 text-vault-text font-bold">
                                {res.room_number || res.room_type || 'Any'}
                              </td>
                              <td className="py-2 px-3 text-vault-text">{res.guest_name}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">{res.email || '-'}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">{res.phone || '-'}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">{res.check_in_date || '-'}</td>
                              <td className="py-2 px-3 text-vault-text-secondary">{res.check_out_date || '-'}</td>
                              <td className="py-2 px-3 text-green-400 font-medium">${res.total_amount || res.total_revenue || 0}</td>
                              <td className="py-2 px-3">
                                {res.confirmation_status === 'confirmed' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                                    <CheckCircle className="w-3 h-3" /> Confirmed
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs font-medium">
                                    <Clock className="w-3 h-3" /> Pending
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-3 flex gap-1">
                                {res.email && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                                    onClick={() => handleSendConfirmationEmail(res.id, res.email)}
                                    title="Send confirmation email"
                                  >
                                    <Mail className="w-4 h-4" />
                                  </Button>
                                )}
                                {res.confirmation_status !== 'confirmed' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30"
                                    onClick={() => handleConfirmReservation(res.id)}
                                    title="Mark as phone confirmed"
                                  >
                                    <Phone className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                                  onClick={() => handleEditGuest(res)}
                                  title="Edit reservation"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-green-400 hover:text-green-300 hover:bg-green-900/30"
                                  onClick={() => openCheckinDialog(res)}
                                  title="Check in guest"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                  onClick={() => handleCancelReservation(res.id || res._id)}
                                  title="Cancel reservation"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Check-in Reservation Dialog */}
              <Dialog open={checkinDialog.open} onOpenChange={(open) => setCheckinDialog({...checkinDialog, open})}>
                <DialogContent className="bg-vault-surface border-vault-border">
                  <DialogHeader>
                    <DialogTitle className="text-vault-text flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      Check In Guest
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="bg-vault-bg rounded-lg p-3">
                      <p className="text-vault-text-secondary text-sm">Guest Name</p>
                      <p className="text-vault-text font-medium">{checkinDialog.guestName}</p>
                    </div>
                    <div>
                      <label className="text-xs text-vault-gold uppercase tracking-wider mb-2 block font-medium">
                        Room Number *
                      </label>
                      <Input
                        type="text"
                        value={checkinDialog.roomNumber}
                        onChange={(e) => setCheckinDialog({...checkinDialog, roomNumber: e.target.value})}
                        placeholder="Enter room number (e.g., 101)"
                        className="bg-vault-bg border-vault-border text-vault-text"
                        data-testid="checkin-room-number-input"
                      />
                      <p className="text-vault-text-secondary text-xs mt-1">
                        Enter the room number to assign to this guest
                      </p>
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setCheckinDialog({ open: false, reservationId: null, guestName: '', roomNumber: '' })}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => handleCheckinReservation(checkinDialog.reservationId, checkinDialog.roomNumber)}
                      disabled={!checkinDialog.roomNumber}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="confirm-checkin-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Check In
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Non-Railroad Guest History Section */}
              <Card className="bg-vault-surface border-vault-border mb-6" data-testid="non-railroad-history-card">
                <CardHeader className="border-b border-vault-border">
                  <div className="flex justify-between items-center flex-wrap gap-3">
                    <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2">
                      <Users className="w-5 h-5 text-amber-400" />
                      Non-Railroad Guest History
                    </CardTitle>
                    <div className="flex gap-2 items-center flex-wrap">
                      {showNonRailroadHistory && (
                        <>
                          {/* Per-card date filter — does NOT inherit the admin-wide
                              startDate/endDate; this card stands on its own. */}
                          <input
                            type="date"
                            value={nonRailStartDate}
                            onChange={(e) => setNonRailStartDate(e.target.value)}
                            className="bg-black/30 border border-vault-border rounded px-2 py-1 text-sm text-vault-text"
                            placeholder="Start"
                            data-testid="non-rail-start-date"
                            aria-label="Non-railroad history start date"
                          />
                          <span className="text-vault-text-secondary text-sm">to</span>
                          <input
                            type="date"
                            value={nonRailEndDate}
                            onChange={(e) => setNonRailEndDate(e.target.value)}
                            className="bg-black/30 border border-vault-border rounded px-2 py-1 text-sm text-vault-text"
                            placeholder="End"
                            data-testid="non-rail-end-date"
                            aria-label="Non-railroad history end date"
                          />
                          <Button
                            onClick={() => fetchNonRailroadHistory(nonRailStartDate, nonRailEndDate)}
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="non-rail-apply-filter"
                          >
                            Apply
                          </Button>
                          {(nonRailStartDate || nonRailEndDate) && (
                            <Button
                              onClick={() => {
                                setNonRailStartDate("");
                                setNonRailEndDate("");
                                fetchNonRailroadHistory();
                              }}
                              size="sm"
                              variant="ghost"
                              className="text-vault-text-secondary"
                              data-testid="non-rail-clear-filter"
                            >
                              Clear
                            </Button>
                          )}
                        </>
                      )}
                      {!showNonRailroadHistory ? (
                        <Button
                          onClick={() => {
                            fetchNonRailroadHistory();
                            setShowNonRailroadHistory(true);
                          }}
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View History
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setShowNonRailroadHistory(false)}
                          size="sm"
                          variant="ghost"
                          className="text-vault-text-secondary"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Hide
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-vault-text-secondary mt-1">
                    View all walk-in and non-railroad guest check-ins (active and checked out){showNonRailroadHistory ? ` — showing ${nonRailroadHistory.length} record${nonRailroadHistory.length === 1 ? '' : 's'}` : ''}
                  </p>
                </CardHeader>
                {showNonRailroadHistory && (
                  <CardContent className="p-0">
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="table-header border-vault-border hover:bg-transparent">
                            <TableHead className="text-amber-400">Guest Name</TableHead>
                            <TableHead className="text-amber-400">Room</TableHead>
                            <TableHead className="text-amber-400">Check-In</TableHead>
                            <TableHead className="text-amber-400">Check-Out</TableHead>
                            <TableHead className="text-amber-400">Rate/Night</TableHead>
                            <TableHead className="text-amber-400">Total</TableHead>
                            <TableHead className="text-amber-400">Contact</TableHead>
                            <TableHead className="text-amber-400">Status</TableHead>
                            <TableHead className="text-amber-400">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nonRailroadHistory.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} className="text-center text-vault-text-secondary py-8">
                                No non-railroad guest history found.
                              </TableCell>
                            </TableRow>
                          ) : (
                            nonRailroadHistory.map((guest) => (
                              <TableRow key={guest.id} className="table-row border-vault-border">
                                <TableCell className="font-medium text-vault-text">{guest.guest_name}</TableCell>
                                <TableCell className="font-mono text-vault-gold font-bold">{guest.room_number || '-'}</TableCell>
                                <TableCell className="text-vault-text-secondary">{guest.check_in_date}</TableCell>
                                <TableCell className="text-vault-text-secondary">{guest.check_out_date || '-'}</TableCell>
                                <TableCell className="text-green-400 font-mono">${guest.room_rate || 0}</TableCell>
                                <TableCell className="text-green-400 font-bold font-mono">${guest.total_revenue || 0}</TableCell>
                                <TableCell className="text-vault-text-secondary text-sm">
                                  {guest.email && <div>{guest.email}</div>}
                                  {guest.phone && <div>{guest.phone}</div>}
                                  {!guest.email && !guest.phone && '-'}
                                </TableCell>
                                <TableCell>
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    guest.is_active 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : 'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {guest.is_active ? 'Checked In' : 'Checked Out'}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    {!guest.is_active && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30 h-7 px-2"
                                        data-testid={`undo-checkout-${guest.id}`}
                                        title="Undo checkout (check this guest back in)"
                                        onClick={async () => {
                                          const currentRoom = guest.room_number || '';
                                          const targetRoom = window.prompt(
                                            `Undo checkout for ${guest.guest_name}.\n\nReassign to which room? (press Enter to reuse Room ${currentRoom})`,
                                            currentRoom
                                          );
                                          if (targetRoom === null) return; // cancelled
                                          try {
                                            await axios.post(
                                              `${API}/admin/non-railroad/${guest.id}/undo-checkout`,
                                              { room_number: (targetRoom || currentRoom).trim() }
                                            );
                                            toast.success(`${guest.guest_name} checked back in to Room ${(targetRoom || currentRoom).trim()}`);
                                            fetchNonRailroadHistory();
                                            fetchStats();
                                          } catch (err) {
                                            toast.error(err?.response?.data?.detail || 'Failed to undo checkout');
                                          }
                                        }}
                                      >
                                        <ArrowLeftRight className="w-4 h-4" />
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-400 hover:text-red-300 hover:bg-red-900/30 h-7 px-2"
                                      onClick={async () => {
                                        if (window.confirm(`Are you sure you want to cancel/delete the booking for ${guest.guest_name}? This will remove it from revenue calculations.`)) {
                                          try {
                                            await axios.delete(`${API}/admin/non-railroad/${guest.id}`);
                                            toast.success('Booking cancelled successfully');
                                            fetchNonRailroadHistory();
                                            fetchStats(); // Refresh revenue stats
                                          } catch (err) {
                                            toast.error('Failed to cancel booking');
                                          }
                                        }
                                      }}
                                      title="Cancel/Delete this booking"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-orange-400 hover:text-orange-300 hover:bg-orange-900/30 h-7 px-2"
                                      onClick={() => handleQuickBlockGuest(guest)}
                                      title="Block this guest from future stays"
                                    >
                                      <Ban className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                )}
              </Card>

              {/* Bitsy AI Bookings Section */}
              <Card className="bg-blue-900/10 border-blue-600/30 mb-6" data-testid="bitsy-bookings-card">
                <CardHeader className="border-b border-blue-600/30">
                  <div className="flex justify-between items-center">
                    <CardTitle className="font-outfit text-xl text-blue-400 flex items-center gap-2">
                      <MessageCircle className="w-5 h-5" />
                      Bitsy AI Bookings
                      <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full text-sm font-normal ml-2">
                        {bitsyBookings.length}
                      </span>
                    </CardTitle>
                    <div className="flex gap-2">
                      {!showBitsyBookings ? (
                        <Button
                          onClick={() => {
                            fetchBitsyBookings();
                            setShowBitsyBookings(true);
                          }}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Bookings
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setShowBitsyBookings(false)}
                          size="sm"
                          variant="ghost"
                          className="text-vault-text-secondary"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Hide
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-blue-300/70 mt-1">
                    Reservations made through the Bitsy AI assistant on your website
                  </p>
                </CardHeader>
                {showBitsyBookings && (
                  <CardContent className="p-0">
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="table-header border-vault-border hover:bg-transparent">
                            <TableHead className="text-blue-400">Confirmation</TableHead>
                            <TableHead className="text-blue-400">Guest Name</TableHead>
                            <TableHead className="text-blue-400">Phone</TableHead>
                            <TableHead className="text-blue-400">Room Type</TableHead>
                            <TableHead className="text-blue-400">Check-In</TableHead>
                            <TableHead className="text-blue-400">Check-Out</TableHead>
                            <TableHead className="text-blue-400">Rate</TableHead>
                            <TableHead className="text-blue-400">Total</TableHead>
                            <TableHead className="text-blue-400">Status</TableHead>
                            <TableHead className="text-blue-400 text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bitsyBookings.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={10} className="text-center text-vault-text-secondary py-8">
                                No Bitsy bookings found. Bookings made through the AI assistant will appear here.
                              </TableCell>
                            </TableRow>
                          ) : (
                            bitsyBookings.map((booking) => (
                              <TableRow key={booking.id} className="table-row border-vault-border">
                                <TableCell className="font-mono text-blue-400 font-bold">{booking.confirmation_number}</TableCell>
                                <TableCell className="font-medium text-vault-text">{booking.guest_name}</TableCell>
                                <TableCell className="text-vault-text-secondary">{booking.phone}</TableCell>
                                <TableCell className="text-vault-text capitalize">{booking.room_type || 'Standard'}</TableCell>
                                <TableCell className="text-vault-text-secondary">{booking.check_in_date}</TableCell>
                                <TableCell className="text-vault-text-secondary">{booking.check_out_date}</TableCell>
                                <TableCell className="text-green-400 font-mono">
                                  {booking.crypto_discount_applied && (
                                    <span className="text-amber-400 text-xs block">10% off</span>
                                  )}
                                  ${booking.rate_per_night || 0}/night
                                </TableCell>
                                <TableCell className="text-green-400 font-mono font-bold">${booking.total_amount || 0}</TableCell>
                                <TableCell>
                                  <div className="space-y-1">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      booking.status === 'confirmed' 
                                        ? 'bg-emerald-500/20 text-emerald-400' 
                                        : booking.status === 'cancelled'
                                        ? 'bg-red-500/20 text-red-400'
                                        : booking.status === 'pending_confirmation'
                                        ? 'bg-orange-500/20 text-orange-400'
                                        : 'bg-amber-500/20 text-amber-400'
                                    }`}>
                                      {booking.status === 'pending_confirmation' ? '⚠️ Needs Call' : (booking.status || 'pending')}
                                    </span>
                                    {booking.requires_phone_confirmation && booking.status !== 'confirmed' && (
                                      <p className="text-orange-400 text-xs">Call to confirm</p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  {booking.status !== 'cancelled' && (
                                    <div className="flex justify-end gap-2">
                                      {booking.status === 'pending_confirmation' && (
                                        <Button
                                          size="sm"
                                          onClick={async () => {
                                            try {
                                              await axios.put(`${API}/admin/bitsy-bookings/${booking.confirmation_number}`, { status: 'confirmed' });
                                              toast.success("Booking confirmed!");
                                              fetchBitsyBookings();
                                            } catch (error) {
                                              toast.error("Failed to confirm booking");
                                            }
                                          }}
                                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                          data-testid={`confirm-bitsy-${booking.confirmation_number}`}
                                        >
                                          <Check className="w-4 h-4 mr-1" />
                                          Confirm
                                        </Button>
                                      )}
                                      {booking.status === 'confirmed' && (
                                        <Button
                                          size="sm"
                                          onClick={() => {
                                            setCheckingInBitsyBooking(booking);
                                            setBitsyCheckInRoom("");
                                          }}
                                          className="bg-blue-600 hover:bg-blue-700 text-white"
                                          data-testid={`checkin-bitsy-${booking.confirmation_number}`}
                                        >
                                          <LogIn className="w-4 h-4 mr-1" />
                                          Check In
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditBitsyBooking(booking)}
                                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                                        data-testid={`edit-bitsy-${booking.confirmation_number}`}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleCancelBitsyBooking(booking.confirmation_number)}
                                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                        data-testid={`cancel-bitsy-${booking.confirmation_number}`}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                )}
              </Card>

              {/* Edit Bitsy Booking Dialog */}
              <Dialog open={!!editingBitsyBooking} onOpenChange={(open) => !open && setEditingBitsyBooking(null)}>
                <DialogContent className="bg-vault-surface border-vault-border">
                  <DialogHeader>
                    <DialogTitle className="text-vault-text">
                      Edit Booking: {editingBitsyBooking?.confirmation_number}
                    </DialogTitle>
                    <DialogDescription className="text-vault-text-secondary">
                      Update the booking details below.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="vault-label">Guest Name</label>
                        <Input
                          value={bitsyEditForm.guest_name}
                          onChange={(e) => setBitsyEditForm({...bitsyEditForm, guest_name: e.target.value})}
                          className="vault-input"
                        />
                      </div>
                      <div>
                        <label className="vault-label">Phone</label>
                        <Input
                          value={bitsyEditForm.phone}
                          onChange={(e) => setBitsyEditForm({...bitsyEditForm, phone: e.target.value})}
                          className="vault-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="vault-label">Email</label>
                        <Input
                          value={bitsyEditForm.email}
                          onChange={(e) => setBitsyEditForm({...bitsyEditForm, email: e.target.value})}
                          className="vault-input"
                        />
                      </div>
                      <div>
                        <label className="vault-label">Room Type</label>
                        <select
                          value={bitsyEditForm.room_type}
                          onChange={(e) => setBitsyEditForm({...bitsyEditForm, room_type: e.target.value})}
                          className="vault-input w-full h-10 px-3"
                        >
                          <option value="single">Single Bed ($79 + tax)</option>
                          <option value="double">Two Beds ($89 + tax)</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="vault-label">Check-In Date</label>
                        <Input
                          type="date"
                          value={bitsyEditForm.check_in_date}
                          onChange={(e) => setBitsyEditForm({...bitsyEditForm, check_in_date: e.target.value})}
                          className="vault-input"
                        />
                      </div>
                      <div>
                        <label className="vault-label">Check-Out Date</label>
                        <Input
                          type="date"
                          value={bitsyEditForm.check_out_date}
                          onChange={(e) => setBitsyEditForm({...bitsyEditForm, check_out_date: e.target.value})}
                          className="vault-input"
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setEditingBitsyBooking(null)}
                      className="border-vault-border text-vault-text"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveBitsyBooking}
                      className="vault-btn-primary"
                    >
                      Save Changes
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Check In Bitsy Booking Dialog */}
              <Dialog open={!!checkingInBitsyBooking} onOpenChange={(open) => !open && setCheckingInBitsyBooking(null)}>
                <DialogContent className="bg-vault-surface border-vault-border">
                  <DialogHeader>
                    <DialogTitle className="text-vault-text flex items-center gap-2">
                      <LogIn className="w-5 h-5 text-blue-400" />
                      Check In Guest
                    </DialogTitle>
                    <DialogDescription className="text-vault-text-secondary">
                      Assign a room to {checkingInBitsyBooking?.guest_name}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    {/* Guest Info Summary */}
                    <div className="bg-black/30 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-vault-text-secondary">Guest:</span>
                        <span className="text-vault-text font-medium">{checkingInBitsyBooking?.guest_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-vault-text-secondary">Phone:</span>
                        <span className="text-vault-text">{checkingInBitsyBooking?.phone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-vault-text-secondary">Confirmation:</span>
                        <span className="text-blue-400 font-mono">{checkingInBitsyBooking?.confirmation_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-vault-text-secondary">Room Type:</span>
                        <span className="text-vault-text capitalize">{checkingInBitsyBooking?.room_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-vault-text-secondary">Dates:</span>
                        <span className="text-vault-text">{checkingInBitsyBooking?.check_in_date} → {checkingInBitsyBooking?.check_out_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-vault-text-secondary">Total:</span>
                        <span className="text-green-400 font-bold">${checkingInBitsyBooking?.total_amount}</span>
                      </div>
                    </div>
                    
                    {/* Room Selection */}
                    <div>
                      <label className="vault-label">Assign Room Number *</label>
                      <select
                        value={bitsyCheckInRoom}
                        onChange={(e) => setBitsyCheckInRoom(e.target.value)}
                        className="vault-input w-full h-12 px-3"
                        data-testid="bitsy-checkin-room-select"
                      >
                        <option value="">Select a room...</option>
                        {rooms
                          .filter(room => room.status === 'available')
                          .map(room => (
                            <option key={room.room_number} value={room.room_number}>
                              Room {room.room_number} - {room.type || room.room_type || 'Standard'}
                            </option>
                          ))
                        }
                      </select>
                      <p className="text-vault-text-secondary text-xs mt-1">
                        Only showing available/clean rooms
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setCheckingInBitsyBooking(null)}
                      className="border-vault-border text-vault-text"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCheckInBitsyBooking}
                      disabled={!bitsyCheckInRoom}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="confirm-bitsy-checkin-btn"
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      Check In to Room {bitsyCheckInRoom}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Blocked Guests (Blacklist) Section */}
              <Card className="bg-red-900/10 border-red-600/30 mb-6" data-testid="blocked-guests-card">
                <CardHeader className="border-b border-red-600/30">
                  <div className="flex justify-between items-center">
                    <CardTitle className="font-outfit text-xl text-red-400 flex items-center gap-2">
                      <Ban className="w-5 h-5" />
                      Blocked Guests (Blacklist)
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setBlockGuestDialog(true)}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add to Blacklist
                      </Button>
                      {!showBlockedGuests ? (
                        <Button
                          onClick={() => {
                            fetchBlockedGuests();
                            setShowBlockedGuests(true);
                          }}
                          size="sm"
                          variant="outline"
                          className="border-red-600/50 text-red-400 hover:bg-red-900/30"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View List
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setShowBlockedGuests(false)}
                          size="sm"
                          variant="ghost"
                          className="text-vault-text-secondary"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Hide
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-red-300/70 mt-1">
                    Guests on this list will be politely declined for future bookings and check-ins
                  </p>
                </CardHeader>
                {showBlockedGuests && (
                  <CardContent className="p-0">
                    <ScrollArea className="h-[300px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="table-header border-red-600/30 hover:bg-transparent">
                            <TableHead className="text-red-400">Guest Name</TableHead>
                            <TableHead className="text-red-400">Email</TableHead>
                            <TableHead className="text-red-400">Phone</TableHead>
                            <TableHead className="text-red-400">Reason</TableHead>
                            <TableHead className="text-red-400">Blocked On</TableHead>
                            <TableHead className="text-red-400">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {blockedGuests.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center text-vault-text-secondary py-8">
                                No blocked guests. Your blacklist is empty.
                              </TableCell>
                            </TableRow>
                          ) : (
                            blockedGuests.map((guest) => (
                              <TableRow key={guest.id} className="table-row border-red-600/20">
                                <TableCell className="font-medium text-vault-text">{guest.guest_name}</TableCell>
                                <TableCell className="text-vault-text-secondary">{guest.email || '-'}</TableCell>
                                <TableCell className="text-vault-text-secondary">{guest.phone || '-'}</TableCell>
                                <TableCell className="text-red-300 text-sm max-w-[200px] truncate" title={guest.reason}>
                                  {guest.reason}
                                </TableCell>
                                <TableCell className="text-vault-text-secondary text-sm">
                                  {guest.blocked_at ? new Date(guest.blocked_at).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-green-400 hover:text-green-300 hover:bg-green-900/30 h-7 px-2"
                                    onClick={() => handleUnblockGuest(guest.id)}
                                    title="Remove from blacklist"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </CardContent>
                )}
              </Card>

              {/* Rooms Table */}
              <Card className="bg-vault-surface border-vault-border" data-testid="rooms-table-card">
                <CardHeader className="border-b border-vault-border">
                  <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2">
                    <Bed className="w-5 h-5 text-vault-gold" />
                    All Rooms
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto text-vault-text-secondary hover:text-vault-gold"
                      onClick={() => setRoomSortOrder(roomSortOrder === 'asc' ? 'desc' : 'asc')}
                      title={`Sort by room number (${roomSortOrder === 'asc' ? 'Ascending' : 'Descending'})`}
                    >
                      {roomSortOrder === 'asc' ? (
                        <><ArrowUp className="w-4 h-4 mr-1" /> Low to High</>
                      ) : (
                        <><ArrowDown className="w-4 h-4 mr-1" /> High to Low</>
                      )}
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="table-header border-vault-border hover:bg-transparent">
                          <TableHead 
                            className="text-vault-gold cursor-pointer hover:text-amber-300"
                            onClick={() => setRoomSortOrder(roomSortOrder === 'asc' ? 'desc' : 'asc')}
                          >
                            <span className="flex items-center gap-1">
                              Room # <ArrowUpDown className="w-3 h-3" />
                            </span>
                          </TableHead>
                          <TableHead className="text-vault-gold">Type</TableHead>
                          <TableHead className="text-vault-gold">Floor</TableHead>
                          <TableHead className="text-vault-gold">Status</TableHead>
                          <TableHead className="text-vault-gold">Notes</TableHead>
                          <TableHead className="text-vault-gold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rooms.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-vault-text-secondary py-8">
                              No rooms added yet. Click "Add Room" to get started.
                            </TableCell>
                          </TableRow>
                        ) : (
                          [...rooms].sort((a, b) => {
                            const numA = parseInt(a.room_number) || 0;
                            const numB = parseInt(b.room_number) || 0;
                            return roomSortOrder === 'asc' ? numA - numB : numB - numA;
                          }).map((room) => (
                            <TableRow key={room.id} className="table-row border-vault-border" data-testid={`room-row-${room.id}`}>
                              <TableCell className="font-mono text-vault-gold font-bold">{room.room_number}</TableCell>
                              <TableCell className="text-vault-text">{room.room_type}</TableCell>
                              <TableCell className="text-vault-text">{room.floor}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  room.status === 'available' ? 'bg-green-500/20 text-green-400' :
                                  room.status === 'occupied' ? 'bg-amber-500/20 text-amber-400' :
                                  room.status === 'blocked' ? 'bg-amber-500/20 text-amber-400' :
                                  room.status === 'reserved' ? 'bg-yellow-500/20 text-yellow-400' :
                                  room.status === 'dirty' ? 'bg-orange-500/20 text-orange-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>
                                  {room.status === 'available'
                                    ? 'Clean'
                                    : room.status === 'blocked'
                                      ? 'Occupied'
                                      : room.status === 'reserved'
                                        ? 'Reserved'
                                        : room.status.charAt(0).toUpperCase() + room.status.slice(1)}
                                </span>
                              </TableCell>
                              <TableCell className="text-vault-text-secondary text-sm">{room.notes || "-"}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {/* Quick dirty/clean toggle */}
                                  {room.status === 'dirty' && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-green-400 hover:text-green-300 hover:bg-green-900/30 h-8 px-2"
                                      onClick={() => handleMarkRoomClean(room.room_number)}
                                      title="Mark as Clean"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                  {room.status === 'available' && (
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-orange-400 hover:text-orange-300 hover:bg-orange-900/30 h-8 px-2"
                                      onClick={() => handleMarkRoomDirty(room.room_number)}
                                      title="Mark as Dirty"
                                    >
                                      <AlertCircle className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-vault-text-secondary hover:text-vault-gold h-8 w-8 p-0"
                                    onClick={() => handleEditRoom(room)}
                                    data-testid={`edit-room-${room.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-vault-text-secondary hover:text-red-500 h-8 w-8 p-0"
                                    onClick={() => setDeleteRoomConfirm(room)}
                                    data-testid={`delete-room-${room.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
    </>
  );
}
